<template>
	<div v-if="content && fundData" class="morningstar-container">
		<StatBox
			v-show="fundData.ratingOverall"
			:id="`morningstar-rating-${uniqueId}`"
			:label="content.morningstarRating"
			:openTooltipModalInSmall="true"
			:caption="`as of ${date} <br/> ${fundData.categoryName} <span class='--no-wrap'>(${sizeOverall} funds)</span>`"
			:customLabel="
				fundData.ratingOverall
					? `${fundData.ratingOverall} ${content.outOfFiveStars}`
					: ''
			"
		>
			<template slot="customTooltip">
				<MorningstarRatingTooltip
					:content="content"
					:fundData="fundData"
					:sizeOverall="sizeOverall"
				/>
			</template>

			<template slot="customContent">
				<template v-if="fundData.ratingOverall">
					<span
						v-for="(rating, index) in fundData.ratingOverall"
						class="icon icon-star"
						:key="`golden-star${index}`"
					/>
					<span
						v-for="(rating, index) in 5 - fundData.ratingOverall"
						class="icon icon-star --grey"
						:key="`grey-star${index}`"
					/>
				</template>

				<template v-else>
					<span>—</span>
				</template>
			</template>
		</StatBox>
	</div>
</template>

<script>
import * as formattedDate from "@vg-faset/core/js/filters/string/formattedDate";
import * as formattedNum from "@vg-faset/core/js/filters/string/formattedNum";
import StatBox from "../stat-box/StatBox";
import MorningstarRatingTooltip from "./MorningstarRatingTooltip";
import { UniqueId } from "../../../utilities";

/**
 * This component provides the Morningstar statbox with custom tooltip. In mobile it opens the tooltip info in a modal.
 *
 * ## Props definitions
 *
 * ### content (Object)
 *
 * The expected content object is below:
 *
 * ```javascript
 * {
 *   threeYears: "String",
 *   fiveYears: "String",
 *   tenYears: "String",
 *   outOfFiveStars: "String",
 *   morningstarRating: "String",
 *   viewCompleteDisclosures: "String",
 *   ratingDefinitionHeading: "String",
 *   ratingDefinitionText: "String", // this content contains variables wrapped in single brackets that this component replaces ... Text {x} is a variable
 *   howIsItDeterminedHeading: "String",
 *   howIsItDeterminedText: "String",
 *   disclaimerTargetURL: "String" // the URL to the disclaimer further down the page, example '#disclaimer-1'
 * ```
 *
 * ### fundData (Object)
 *
 * ```javascript
 * {
 *   categoryName: "Large Blend",
 *   categorySize3Yr: 1230, // not used
 *   categorySize5Yr: 1058, // not used
 *   categorySize10Yr: 806, // not used
 *   categorySizeOverall: 1230, // the component formats this to string '1,230'
 *   rating3Yr: 4,
 *   rating5Yr: 4,
 *   rating10Yr: 5,
 *   ratingOverall: 5,
 *   ratingAsOfDate: "2020-06-30" // the component formats this to mm/dd/yyyy
 * }
 * ```
 *
 */
export default {
	name: "MorningStar",

	data() {
		return {
			uniqueId: new UniqueId().generate()
		};
	},

	components: {
		StatBox,
		MorningstarRatingTooltip
	},

	props: {
		/**
		 * This object contains all the content strings required for this component. The expected data object is described in the documentation above.
		 */
		content: {
			type: Object,
			default: () => {}
		},

		/**
		 * The Morningstar data that is associated with the fund. If `this.fundData` provides the fund data object used with Statboxes, `this.fundData.morningstar` should provide the Morningstar data. The expected data object is described in the documentation above.
		 */
		fundData: {
			type: Object,
			default: () => {}
		}
	},

	computed: {
		date() {
			return this.fundData.ratingAsOfDate
				? formattedDate.default(this.fundData.ratingAsOfDate)
				: "—";
		},

		sizeOverall() {
			return this.fundData.categorySizeOverall
				? formattedNum.default(this.fundData.categorySizeOverall)
				: "—";
		}
	}
};
</script>

<style lang="scss" scoped>
.custom-label {
	position: relative;
	border-bottom: 1px dashed $color--red-3;
	font-size: 11px;
	line-height: 1.1;
	letter-spacing: 0.09091em;
	text-transform: uppercase;
	cursor: pointer;
}

.morningstar-container {
	.icon-star {
		color: $color--yellow-4;
		display: inline-block;
		font-size: 20px;
		line-height: 24px;
		margin-bottom: 8px;
		vertical-align: middle;

		&.\--grey {
			color: $color--neutral-7;
		}
	}
}

.\--no-wrap {
	white-space: nowrap;
}

.morningstar-modal {
	text-decoration: none;

	@include hover {
		color: $color--dark-red;
		text-decoration: underline;
	}
}
</style>
